import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button, ScoreRing, scoreKindLabel, ChangeList, FixNext, PluginMove, NothingUrgent } from '../ds';
import { analysisCardModel } from '../../utils/review';
import { isFresh } from '../../hooks/analysisStore';

const NO_MOVES_FIRST_NOTE = 'This is the first version. Upload V2 here after any changes to see what improved.';

const LegacyMoves = ({ moves }) => (
  <section className="flex flex-col gap-space-3">
    <h3 className="m-0 font-display text-section text-ink">Suggested moves</h3>
    <ol className="m-0 p-0 list-none flex flex-col gap-space-3">
      {moves.map((move, index) => (
        <li key={`${index}-${move.plugin}`}>
          <PluginMove number={index + 1} {...move} />
        </li>
      ))}
    </ol>
  </section>
);

/**
 * The latest version's analysis, in the design system's order: score and headline, what changed,
 * then the one fix (or Nothing urgent). Versions from before scores show their old suggestions.
 */
export const AnalysisCard = ({ version, onShowMetering }) => {
  const card = analysisCardModel(version);
  // A score that just arrived fills its ring in; read once, so a re-render doesn't replay it
  const [animateIn] = useState(() => isFresh(version.id));
  const cardRef = useRef(null);

  // ...and brings the card's top into view so it's seen landing. The thread scrolls to its end after
  // this card mounts, so wait a frame to scroll after it.
  useEffect(() => {
    if (!animateIn) return undefined;
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
    const frame = requestAnimationFrame(() => cardRef.current?.scrollIntoView?.({ block: 'start', behavior }));
    return () => cancelAnimationFrame(frame);
  }, [animateIn]);
  const isMix = card.scoreKind === 'mastering_readiness';
  const label = card.kind === 'legacy' ? 'Analyzed before scores were added' : scoreKindLabel(card.scoreKind);

  return (
    <article ref={cardRef} aria-label={`V${version.version} analysis`} className="flex flex-col gap-space-7 p-space-6 sm:p-space-8 rounded-radius-xl bg-surface font-text">
      <div className="flex flex-wrap items-center gap-space-6">
        {card.score !== null && (
          <ScoreRing score={card.score} kind={card.scoreKind} previous={card.previousScore} previousVersion={card.previousVersion} animateIn={animateIn} />
        )}
        <div className="flex-[1_1_260px] min-w-0 flex flex-col gap-space-2">
          <span className="text-label text-muted">{label}</span>
          <h2 className="m-0 font-display text-title-1 text-ink break-words">{card.headline}</h2>
          {card.summary && <p className="m-0 text-lead text-ink-soft">{card.summary}</p>}
        </div>
      </div>

      {card.kind !== 'legacy' && (
        <ChangeList
          previousVersion={card.previousVersion}
          changes={card.changes}
          everythingElse={card.everythingElse}
          firstVersionNote={card.nextFix ? undefined : NO_MOVES_FIRST_NOTE}
        />
      )}

      {card.kind === 'review' && (card.nextFix
        ? <FixNext title={card.nextFix.title} why={card.nextFix.why} moves={card.moves} />
        : (
          <NothingUrgent>
            {isMix
              ? 'This mix is ready for mastering. Ask below if you want to push it further.'
              : 'This is ready to sit in the mix. Ask below if you want to push it further.'}
          </NothingUrgent>
        ))}

      {card.kind === 'legacy' && card.moves.length > 0 && <LegacyMoves moves={card.moves} />}

      {onShowMetering && (
        <Button variant="text" icon={ChevronRight} iconPosition="end" onClick={onShowMetering} className="self-start">
          See all measurements
        </Button>
      )}
    </article>
  );
};
